"use client";

import { use, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ArrowLeft,
  Mail,
  Phone,
  Check,
  Icon,
  Badge,
} from "lucide-react";

interface ClientProps {
  solution: any;
}

export default function SolutionDetailPageClient({ solution }: ClientProps) {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(0);

  return (
    <>
      {/* Hero Section */}
      <section className="px-4 py-12 sm:px-6 lg:px-8 bg-primary">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-2 items-center">
            {/* Left: Text */}
            <div className="text-white space-y-5">
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight text-balance">
                {solution.name}
              </h1>
              <p className="text-lg text-white/85 leading-relaxed">
                {solution.description}
              </p>
              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link href="#lien-he">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Liên Hệ Tư Vấn
                </Button>
                </Link>
                <Link href={"tel:0886967375"}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="bg-white/10 border-white/60 text-white hover:bg-white/10 font-semibold flex items-center gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    Gọi Tư Vấn
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Image + Price card */}
            <div className="space-y-4">
              {/* Image */}
              <div
                className="rounded-2xl overflow-hidden border border-white/20 shadow-2xl"
                style={{ height: "100%" }}
              >
                <img
                  src={solution.image.medium}
                  alt={solution.name}
                  className="w-full h-full object-cover"
                />
              </div>

            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-2">
            {/* Left: Detail Content (2/3) */}
            <div className="lg:col-span-2 space-y-12">
              {/* Rich Text Detail */}
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Chi Tiết Giải Pháp
                </h2>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div
                    className="html-content prose prose-slate max-w-none p-6"
                    dangerouslySetInnerHTML={{ __html: solution.content }}
                    style={{
                      lineHeight: "1.7",
                      color: "#334155",
                      minHeight: "200px",
                    }}
                  />
                </div>
              </div>

              {/* Features */}
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Ứng Dụng
                </h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(solution?.features || []).map(
                    (feature: any, idx: number) => (
                      <div
                        key={`solution.features.${idx}`}
                        className="flex items-start gap-3 p-3 rounded-xl"
                        style={{ backgroundColor: "#f8fafc" }}
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                          <Check className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span
                          className="text-sm leading-relaxed"
                          style={{ color: "#475569" }}
                        >
                          {feature}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {/* FAQ */}
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Câu Hỏi Thường Gặp
                </h2>
                <div className="space-y-3">
                  {solution.faqs.map((faq: any, idx: number) => (
                    <div
                      key={`product-${solution.slug}-faqs-${faq.question}`}
                      style={{
                        borderColor: "#e2e8f0",
                        backgroundColor: "#ffffff",
                      }}
                      className="border rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() =>
                          setExpandedFaq(expandedFaq === idx ? null : idx)
                        }
                        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                      >
                        <span className="font-semibold text-foreground text-sm">
                          {faq.question}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 text-primary flex-shrink-0 ml-3 transition-transform duration-200 ${expandedFaq === idx ? "rotate-180" : ""}`}
                        />
                      </button>
                      {expandedFaq === idx && (
                        <div className="px-5 py-4 border-t border-slate-100">
                          <p
                            style={{ color: "#64748b" }}
                            className="text-sm leading-relaxed"
                          >
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
